/**
 * Prix du Canada - Standardized UI Component Library
 *
 * Design Principles:
 * - Headings: Center-aligned, elegant typography
 * - Form fields: Left-aligned (standard UX)
 * - Body text in cards: Center-aligned for elegance
 * - Consistent gold accents and spacing
 * - Modern, interactive components
 */

import React from 'react';
import { Container, Title, Text, Paper, Button, Stack, Group, Box } from '@mantine/core';

export { default as ConsentCheckbox } from './ConsentCheckbox';

// ============================================
// PAGE SECTIONS
// ============================================

/**
 * PageSection - Standard section wrapper with consistent spacing
 * Usage: <PageSection bg="light">...</PageSection>
 */
export const PageSection = ({
  children,
  bg = 'white', // 'white', 'light', 'cream', 'gradient'
  centered = false,
  py = 'xl', // xs, sm, md, lg, xl
  ...props
}) => {
  const backgrounds = {
    white: '#ffffff',
    light: '#f8f6f3',
    cream: '#faf8f5',
    gradient: 'linear-gradient(135deg, #c9a96e 0%, #b8956f 100%)'
  };

  return (
    <Box
      component="section"
      py={py === 'xl' ? 80 : py === 'lg' ? 60 : py === 'md' ? 40 : py === 'sm' ? 30 : 20}
      style={{
        background: backgrounds[bg] || bg,
        textAlign: centered ? 'center' : 'left'
      }}
      {...props}
    >
      <Container size="lg">
        {children}
      </Container>
    </Box>
  );
};

// ============================================
// TYPOGRAPHY
// ============================================

/**
 * PageTitle - Main page heading (always centered)
 * Usage: <PageTitle>Welcome to Prix du Canada</PageTitle>
 */
export const PageTitle = ({ children, color = 'gold', mb = 'md', ...props }) => (
  <Title
    order={1}
    ta="center"
    mb={mb}
    style={{
      fontFamily: "'Playfair Display', serif",
      fontSize: '3.2rem',
      fontWeight: 800,
      color: color === 'gold' ? '#c9a96e' : color === 'dark' ? '#2c2c2c' : color,
      letterSpacing: '-0.01em',
      lineHeight: 1.3
    }}
    {...props}
  >
    {children}
  </Title>
);

/**
 * SectionTitle - Section heading (always centered)
 * Usage: <SectionTitle>Your Stage Awaits</SectionTitle>
 */
export const SectionTitle = ({ children, color = 'gold', mb = 'xl', ...props }) => (
  <Title
    order={2}
    ta="center"
    mb={mb}
    style={{
      fontFamily: "'Playfair Display', serif",
      fontSize: '2.4rem',
      fontWeight: 700,
      color: color === 'gold' ? '#c9a96e' : color === 'dark' ? '#2c2c2c' : color,
      letterSpacing: '-0.01em'
    }}
    {...props}
  >
    {children}
  </Title>
);

/**
 * SubTitle - Subsection heading (centered by default)
 * Usage: <SubTitle>Professional Excellence</SubTitle>
 */
export const SubTitle = ({ children, color = 'dark', centered = true, mb = 'md', ...props }) => (
  <Title
    order={3}
    ta={centered ? 'center' : 'left'}
    mb={mb}
    style={{
      fontFamily: "'Playfair Display', serif",
      fontSize: '1.8rem',
      fontWeight: 600,
      color: color === 'gold' ? '#c9a96e' : color === 'dark' ? '#2c2c2c' : color
    }}
    {...props}
  >
    {children}
  </Title>
);

/**
 * BodyText - Standard paragraph text
 * Usage: <BodyText centered>Your description here</BodyText>
 */
export const BodyText = ({ children, centered = false, size = 'md', color = 'dark', ...props }) => (
  <Text
    ta={centered ? 'center' : 'left'}
    size={size}
    style={{
      fontFamily: "'Lora', serif",
      fontSize: size === 'lg' ? '1.2rem' : size === 'md' ? '1.1rem' : '1rem',
      lineHeight: 1.8,
      color: color === 'dark' ? '#2c2c2c' : color === 'gray' ? '#666' : color,
      fontWeight: 500
    }}
    {...props}
  >
    {children}
  </Text>
);

// ============================================
// CARDS & CONTAINERS
// ============================================

/**
 * FeatureCard - Card with centered content (for features/benefits)
 * Usage: <FeatureCard icon="✨" title="Every Dancer Performs">content</FeatureCard>
 */
export const FeatureCard = ({
  icon,
  title,
  children,
  goldTop = false,
  bg = 'white',
  ...props
}) => (
  <Paper
    p="xl"
    radius="md"
    shadow="sm"
    style={{
      background: bg === 'cream' ? '#faf8f5' : bg === 'light' ? '#f8f6f3' : '#ffffff',
      borderTop: goldTop ? '4px solid #c9a96e' : 'none',
      textAlign: 'center',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(201, 169, 110, 0.15)'
      }
    }}
    {...props}
  >
    {icon && (
      <Box
        style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #c9a96e, #b8956f)',
          borderRadius: '50%',
          margin: '0 auto 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <span style={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>{icon}</span>
      </Box>
    )}

    {title && (
      <SubTitle mb="md" color="gold">{title}</SubTitle>
    )}

    <BodyText centered>{children}</BodyText>
  </Paper>
);

/**
 * FormCard - Card for forms with left-aligned content
 * Usage: <FormCard title="Sign In">form fields</FormCard>
 */
export const FormCard = ({
  title,
  subtitle,
  children,
  maxWidth = 500,
  centered = true,
  ...props
}) => (
  <Box style={{ maxWidth, margin: centered ? '0 auto' : '0' }}>
    <Paper
      p="xl"
      radius="md"
      shadow="md"
      style={{
        background: '#ffffff',
        border: '1px solid #f0f0f0'
      }}
      {...props}
    >
      {title && (
        <SubTitle mb="sm" centered>{title}</SubTitle>
      )}

      {subtitle && (
        <Text
          ta="center"
          size="sm"
          c="dimmed"
          mb="xl"
          style={{
            fontFamily: "'Lora', serif"
          }}
        >
          {subtitle}
        </Text>
      )}

      <Stack gap="md">
        {children}
      </Stack>
    </Paper>
  </Box>
);

// ============================================
// BUTTONS
// ============================================

// Mobile-friendly minimum heights (Apple: 44px, Google: 48px minimum for tap targets)
const BUTTON_HEIGHTS = { xs: 36, sm: 40, md: 48, lg: 56 };

/**
 * PrimaryButton - Main CTA button with gold gradient
 * - Consistent min-height across the app (mobile-friendly tap targets)
 * - whiteSpace: nowrap prevents text vertical squishing
 * - Hover/disabled states via Mantine `styles` prop (inline `style` doesn't support pseudo-selectors)
 *
 * Accepts all Mantine Button props (loading, leftSection, rightSection, fullWidth, size, type, onClick, disabled, etc.)
 */
export const PrimaryButton = ({ children, size = 'md', styles: userStyles, ...props }) => (
  <Button
    size={size}
    {...props}
    styles={{
      ...userStyles,
      root: {
        background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
        border: 'none',
        color: '#fff',
        minHeight: BUTTON_HEIGHTS[size] ?? 48,
        fontWeight: 600,
        letterSpacing: '0.3px',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease',
        '&:hover:not(:disabled)': {
          filter: 'brightness(0.95)',
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(201, 169, 110, 0.35)'
        },
        '&:active:not(:disabled)': {
          transform: 'translateY(0)'
        },
        '&:disabled': {
          opacity: 0.6,
          background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
          color: '#fff'
        },
        ...userStyles?.root
      },
      label: {
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        overflow: 'visible',
        ...userStyles?.label
      }
    }}
  >
    {children}
  </Button>
);

/**
 * SecondaryButton - Outlined gold button (paired with PrimaryButton)
 * Same size/height scale as PrimaryButton so they line up.
 */
export const SecondaryButton = ({ children, size = 'md', styles: userStyles, ...props }) => (
  <Button
    size={size}
    variant="outline"
    {...props}
    styles={{
      ...userStyles,
      root: {
        background: 'transparent',
        borderColor: 'var(--primary-gold)',
        borderWidth: 2,
        color: 'var(--primary-gold)',
        minHeight: BUTTON_HEIGHTS[size] ?? 48,
        fontWeight: 600,
        letterSpacing: '0.3px',
        transition: 'background 0.15s ease, color 0.15s ease, transform 0.15s ease',
        '&:hover:not(:disabled)': {
          background: 'var(--primary-gold)',
          color: '#fff',
          transform: 'translateY(-1px)'
        },
        '&:disabled': { opacity: 0.6 },
        ...userStyles?.root
      },
      label: {
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        overflow: 'visible',
        ...userStyles?.label
      }
    }}
  >
    {children}
  </Button>
);

// ============================================
// SPECIAL COMPONENTS
// ============================================

/**
 * CTABox - Highlighted call-to-action box
 * Usage: <CTABox title="Special Offer">content</CTABox>
 */
export const CTABox = ({ title, children, ...props }) => (
  <Paper
    p="xl"
    radius="md"
    style={{
      background: '#f8f6f3',
      border: '2px solid #c9a96e',
      maxWidth: '600px',
      margin: '0 auto',
      textAlign: 'center'
    }}
    {...props}
  >
    {title && (
      <Title
        order={3}
        mb="md"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.4rem',
          color: '#c9a96e'
        }}
      >
        {title}
      </Title>
    )}

    <Stack gap="lg" align="center">
      {children}
    </Stack>
  </Paper>
);

/**
 * IconCircle - Circular icon background
 * Usage: <IconCircle>✨</IconCircle>
 */
export const IconCircle = ({ children, size = 80, ...props }) => (
  <Box
    style={{
      width: size,
      height: size,
      background: 'linear-gradient(135deg, #c9a96e, #b8956f)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto'
    }}
    {...props}
  >
    <span style={{ color: 'white', fontSize: `${size * 0.4}px`, fontWeight: 'bold' }}>
      {children}
    </span>
  </Box>
);

// ============================================
// LAYOUT HELPERS
// ============================================

/**
 * CenteredStack - Vertically stacked centered content
 * Usage: <CenteredStack>items</CenteredStack>
 */
export const CenteredStack = ({ children, gap = 'md', ...props }) => (
  <Stack gap={gap} align="center" {...props}>
    {children}
  </Stack>
);

/**
 * CenteredGroup - Horizontally grouped centered content
 * Usage: <CenteredGroup>items</CenteredGroup>
 */
export const CenteredGroup = ({ children, gap = 'md', ...props }) => (
  <Group gap={gap} justify="center" {...props}>
    {children}
  </Group>
);
